import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { API_BASE_URL } from "../../config";

interface UserData {
  name: string;
  email: string;
  userId: string;
  role: string;
}

interface Candidate {
  id: string;
  name: string;
  phoneNo: string;
  education: string;
  image: string;
  createDateTime: string;
  lastUpdatedDateTime: string;
  totalMarks?: number;
  status?: "Pass" | "Fail" | "Pending";
}

interface LoginResponse {
  success: boolean;
  message: string;
  data: {
    username: string;
    email: string;
    userId: string;
    role: string;
  };
}

interface MarksResponse {
  success: boolean;
  message: string;
  data: {
    id: string;
    totalMarks: number;
    logicalTotal: number;
    memorizationTotal: number;
    aptitudeTotal: number;
    listeningTotal: number;
    writingTotal: number;
    name: string;
    phoneNo: string;
    education: string;
  };
}

const Dashboard = () => {
  const [userId] = useState<string>("123");
  const [userData, setUserData] = useState<UserData | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [isDropdownOpen, setIsDropdownOpen] = useState<boolean>(false);

  const [candidates, setCandidates] = useState<Candidate[]>([]);
  const [filteredCandidates, setFilteredCandidates] = useState<Candidate[]>([]);
  const [candidatesLoading, setCandidatesLoading] = useState<boolean>(true);
  const [candidatesError, setCandidatesError] = useState<string | null>(null);

  // Filter states
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [educationFilter, setEducationFilter] = useState<string>("");
  const [statusFilter, setStatusFilter] = useState<string>("");
  const [uniqueEducations, setUniqueEducations] = useState<string[]>([]);

  const navigate = useNavigate();

  // Role-based redirect function
  const redirectToLogin = () => {
    window.location.href = "/trainer/login";
  };

  const performLogout = useCallback(() => {
    // Clear user data from localStorage
    localStorage.removeItem("userId");
    localStorage.removeItem("userToken"); // if you store tokens

    // Redirect to login
    redirectToLogin();
  }, []);

  // Fetch user data
  useEffect(() => {
    const fetchUserData = async () => {
      try {
        setLoading(true);
        const userId = localStorage.getItem("userId");

        if (!userId) {
          // No userId in localStorage, redirect to login
          redirectToLogin();
          return;
        }

        const response = await fetch(
          `${API_BASE_URL}/login/user/${userId}`
        );

        if (!response.ok) {
          throw new Error("Failed to fetch user data");
        }

        const result: LoginResponse = await response.json();
        if (result.success && result.data) {
          const user = {
            name: result.data.username,
            email: result.data.email,
            userId: result.data.userId,
            role: result.data.role,
          };

          // Check if user role is trainer (case-insensitive)
          if (user.role.toLowerCase() !== "trainer") {
            // User is not a trainer, perform logout and redirect
            alert("Access denied. Only trainers can access this dashboard.");
            performLogout();
            return;
          }

          setUserData(user);
        } else {
          throw new Error(result.message || "Failed to fetch user data");
        }
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : "Unknown error occurred";
        console.error("Error fetching user data:", errorMessage);

        // On error, also redirect to login
        alert("Authentication failed. Please login again.");
        performLogout();
      } finally {
        setLoading(false);
      }
    };

    fetchUserData();
  }, [performLogout, userId]);

  // Fetch marks for a candidate
  const fetchCandidateMarks = async (
    candidateId: string
  ): Promise<{
    totalMarks: number;
    status: "Pass" | "Fail" | "Pending";
    examId?: string;
  }> => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/screening-questions/${candidateId}/marks`
      );

      if (!response.ok) {
        // If marks not found, return pending status
        return { totalMarks: 0, status: "Pending" };
      }

      const result: MarksResponse = await response.json();

      if (result.success && result.data) {
        const totalMarks = result.data.totalMarks || 0;
        const listeningTotal = result.data.listeningTotal || 0;
        const examId = result.data.id;

        // Determine status based on listeningTotal and marks
        let status: "Pass" | "Fail" | "Pending";

        if (listeningTotal === 0) {
          status = "Pending";
        } else {
          // Determine status based on marks (you can adjust the passing threshold)
          const passingMarks = 55; // Adjust this threshold as needed
          status = totalMarks >= passingMarks ? "Pass" : "Fail";
        }

        return { totalMarks, status, examId };
      } else {
        throw new Error(result.message || "Failed to fetch marks data");
      }
    } catch (error) {
      console.error(
        `Error fetching marks for candidate ${candidateId}:`,
        error
      );
      return { totalMarks: 0, status: "Pending" };
    }
  };

  // Fetch candidates data
  useEffect(() => {
    // Only fetch candidates if user is authenticated and is a trainer
    if (!userData || userData.role.toLowerCase() !== "trainer") {
      return;
    }

    const fetchCandidates = async () => {
      try {
        setCandidatesLoading(true);
        const response = await fetch(`${API_BASE_URL}/registration`);

        if (!response.ok) {
          throw new Error("Failed to fetch candidates data");
        }

        const data: Candidate[] = await response.json();

        // Fetch marks for each candidate
        const candidatesWithMarks = await Promise.all(
          data.map(async (candidate) => {
            const { totalMarks, status } = await fetchCandidateMarks(
              candidate.id
            );
            return {
              ...candidate,
              totalMarks,
              status,
            };
          })
        );

        setCandidates(candidatesWithMarks);

        // Extract unique educations for filter
        const educations = Array.from(
          new Set(candidatesWithMarks.map((c) => c.education))
        );
        setUniqueEducations(educations);
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : "Unknown error occurred";
        setCandidatesError(errorMessage);
        console.error("Error fetching candidates:", errorMessage);
      } finally {
        setCandidatesLoading(false);
      }
    };

    fetchCandidates();
  }, [userData]);

  // Filter candidates based on search term, education, and status
  useEffect(() => {
    let filtered = candidates;

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(
        (candidate) =>
          candidate.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          candidate.phoneNo.includes(searchTerm) ||
          candidate.id.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Education filter
    if (educationFilter) {
      filtered = filtered.filter(
        (candidate) => candidate.education === educationFilter
      );
    }

    // Status filter
    if (statusFilter) {
      filtered = filtered.filter(
        (candidate) => candidate.status === statusFilter
      );
    }

    setFilteredCandidates(filtered);
  }, [candidates, searchTerm, educationFilter, statusFilter]);

  const handleLogout = () => {
    setIsDropdownOpen(false);
    performLogout();
  };

  const toggleDropdown = () => {
    setIsDropdownOpen(!isDropdownOpen);
  };

  const getStatusBadge = (status: "Pass" | "Fail" | "Pending") => {
    switch (status) {
      case "Pass":
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
            <svg
              className="h-3 w-3 mr-1"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                clipRule="evenodd"
              />
            </svg>
            Pass
          </span>
        );
      case "Fail":
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
            <svg
              className="h-3 w-3 mr-1"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                clipRule="evenodd"
              />
            </svg>
            Fail
          </span>
        );
      case "Pending":
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
            <svg
              className="h-3 w-3 mr-1"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M3 3a1 1 0 011-1h12a1 1 0 011 1v3a1 1 0 01-.293.707L12 11.414V15a1 1 0 01-.293.707l-2 2A1 1 0 018 17v-5.586L3.293 6.707A1 1 0 013 6V3z"
                clipRule="evenodd"
              />
            </svg>
            Pending
          </span>
        );
      default:
        return null;
    }
  };

  const clearFilters = () => {
    setSearchTerm("");
    setEducationFilter("");
    setStatusFilter("");
  };

  const handleRowClick = async (candidateId: string) => {
    try {
      const { examId } = await fetchCandidateMarks(candidateId);
      if (examId) {
        sessionStorage.setItem("examId", examId);
        sessionStorage.setItem("candidateId", candidateId);
        navigate("/candidates-evaluation");
      }
    } catch (error) {
      console.error("Error handling row click:", error);
    }
  };

  const handleRevaluateClick = async (
    candidateId: string,
    event: React.MouseEvent
  ) => {
    event.stopPropagation(); // Prevent row click
    try {
      const { examId } = await fetchCandidateMarks(candidateId);
      if (examId) {
        sessionStorage.setItem("examId", examId);
        sessionStorage.setItem("candidateId", candidateId);
        navigate("/candidates-evaluation");
      }
    } catch (error) {
      console.error("Error handling revaluate click:", error);
    }
  };

  // Clear examId and candidateId when returning to dashboard
  useEffect(() => {
    sessionStorage.removeItem("examId");
    sessionStorage.removeItem("candidateId");
  }, []);

  if (loading || candidatesLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  // If userData is null at this point, it means we're in the process of redirecting
  if (!userData) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-600">Redirecting to login...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Navbar */}
      <nav className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            {/* Logo */}
            <div className="flex items-center">
              <div className="flex-shrink-0 flex items-center">
                <img
                  src="https://res.cloudinary.com/dureqzdco/image/upload/v1735905040/Image/r1u63eh2cz9nt9kcocr2.png"
                  alt=""
                  className="h-10 w-auto"
                />
              </div>
            </div>

            {/* User Info & Actions */}
            <div className="flex items-center space-x-4">
              {userData && (
                <div className="relative">
                  {/* User Avatar/Button */}
                  <button
                    onClick={toggleDropdown}
                    className="flex items-center space-x-2 px-3 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-50 rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <div className="h-8 w-8 bg-blue-600 rounded-full flex items-center justify-center">
                      <span className="text-white font-medium text-sm">
                        {userData.name.charAt(0).toUpperCase()}
                      </span>
                    </div>
                    <span className="font-medium">{userData.name}</span>
                    <svg
                      className={`h-4 w-4 transition-transform ${
                        isDropdownOpen ? "rotate-180" : ""
                      }`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  </button>

                  {/* Dropdown Menu */}
                  {isDropdownOpen && (
                    <div className="absolute right-0 mt-2 w-64 bg-white rounded-md shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none z-50">
                      <div className="py-1">
                        {/* User Info Section */}
                        <div className="px-4 py-3 border-b border-gray-100">
                          <div className="text-sm font-semibold text-gray-900">
                            {userData.name}
                          </div>
                          <div className="text-sm text-gray-500 mt-1">
                            {userData.email}
                          </div>
                          <div className="flex items-center mt-2">
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                              {userData.role}
                            </span>
                            <span className="ml-2 text-xs text-gray-400">
                              ({userData.userId})
                            </span>
                          </div>
                        </div>

                        {/* Logout Button */}
                        <button
                          onClick={handleLogout}
                          className="w-full text-left px-4 py-3 text-sm text-red-600 hover:bg-red-50 hover:text-red-800 transition-colors flex items-center space-x-2"
                        >
                          <svg
                            className="h-4 w-4"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
                            />
                          </svg>
                          <span className="font-medium">Logout</span>
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Backdrop to close dropdown */}
                  {isDropdownOpen && (
                    <div
                      className="fixed inset-0 z-40"
                      onClick={() => setIsDropdownOpen(false)}
                    />
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Filters Section */}
        <div className="bg-white shadow-sm rounded-lg border border-gray-200 p-6 mb-6">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0 lg:space-x-4">
            {/* Search */}
            <div className="flex-1 max-w-md">
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <svg
                    className="h-5 w-5 text-gray-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                    />
                  </svg>
                </div>
                <input
                  type="text"
                  className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Search by name, phone, or ID..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>

            {/* Filters */}
            <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-4">
              {/* Education Filter */}
              <select
                className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                value={educationFilter}
                onChange={(e) => setEducationFilter(e.target.value)}
              >
                <option value="">All Education</option>
                {uniqueEducations.map((education) => (
                  <option key={education} value={education}>
                    {education}
                  </option>
                ))}
              </select>

              {/* Status Filter */}
              <select
                className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <option value="">All Status</option>
                <option value="Pass">Pass</option>
                <option value="Fail">Fail</option>
                <option value="Pending">Pending</option>
              </select>

              {/* Clear Filters */}
              {(searchTerm || educationFilter || statusFilter) && (
                <button
                  onClick={clearFilters}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  Clear Filters
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Candidates Table */}
        <div className="bg-white shadow-sm rounded-lg border border-gray-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">
              Candidates List
            </h2>
            <div className="flex items-center justify-between mt-1">
              <p className="text-sm text-gray-500">
                Showing {filteredCandidates.length} of {candidates.length}{" "}
                candidates
              </p>
              {candidatesError && (
                <p className="text-sm text-red-600">
                  Error loading candidates: {candidatesError}
                </p>
              )}
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Candidate
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Contact
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Education
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Total Marks
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredCandidates.map((candidate) => (
                  <tr
                    key={candidate.id}
                    className={`transition-colors ${
                      candidate.status === "Fail"
                        ? "hover:bg-gray-50"
                        : "hover:bg-gray-50 cursor-pointer"
                    }`}
                    onClick={() =>
                      candidate.status !== "Fail" &&
                      handleRowClick(candidate.id)
                    }
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="h-12 w-12 flex-shrink-0">
                          <img
                            className="h-12 w-12 rounded-full object-cover border-2 border-gray-200"
                            src={candidate.image}
                            alt={candidate.name}
                            onError={(e) => {
                              const target = e.target as HTMLImageElement;
                              target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(
                                candidate.name
                              )}&background=3b82f6&color=ffffff`;
                            }}
                          />
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">
                            {candidate.name}
                          </div>
                          <div className="text-sm text-gray-500">
                            ID: {candidate.id.slice(0, 8)}...
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center space-x-2 text-sm text-gray-900">
                        <svg
                          className="h-4 w-4 text-gray-400"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                          />
                        </svg>
                        <span>{candidate.phoneNo}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center space-x-2 text-sm text-gray-900">
                        <svg
                          className="h-4 w-4 text-gray-400"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path d="M12 14l9-5-9-5-9 5 9 5z"></path>
                          <path d="M12 14l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14z"></path>
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M12 14l9-5-9-5-9 5 9 5zm0 0l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14z"
                          ></path>
                        </svg>
                        <span>{candidate.education}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {candidate.totalMarks !== undefined
                          ? `${candidate.totalMarks}/90`
                          : "N/A"}
                      </div>
                    </td>
                    <td className="px-2 py-4 whitespace-nowrap">
                      <div className="flex items-center space-x-2">
                        {candidate.status && getStatusBadge(candidate.status)}
                        {candidate.status === "Fail" && (
                          <button
                            onClick={(e) =>
                              handleRevaluateClick(candidate.id, e)
                            }
                            className="inline-flex items-center px-3 py-1.5 rounded-md text-xs font-medium bg-green-100 text-green-800 hover:bg-green-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 transition-colors"
                          >
                            <svg
                              className="h-3 w-3 mr-1"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                              />
                            </svg>
                            Revaluate
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {filteredCandidates.length === 0 && (
          <div className="text-center py-12">
            <div className="text-gray-400 text-lg">
              {candidates.length === 0
                ? "No candidates found"
                : "No candidates match your filters"}
            </div>
            <p className="text-gray-500 mt-2">
              {candidates.length === 0
                ? "Candidates will appear here once they are added to the system."
                : "Try adjusting your search terms or filters to see more results."}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Dashboard;
